import { InputNumber, Modal, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { FC, useEffect, useMemo, useState } from 'react'
import formatNumber from '../../../../../helpers/formatters/format-any-number'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import {
    useGetRewindJobsQuery,
    useUpdateRewindJobsCopiesMutation,
} from '../../../../../store/api/rewind-jobs-api'
import { rewindQueueEntriesApi } from '../../../../../store/api/rewind-queue-entries-api'
import { useAppDispatch } from '../../../../../store/store'
import RewindJob from '../../../../../types/api/rewind-job'
import Shipment from '../../../../../types/api/shipment'
import { DocumentId } from '../../../../../types/strapi/strapi'
import { getRewindJobCopies } from '../../helpers/get-rewind-job-copies'
import { getShipmentCopies } from '../../helpers/get-shipment-copies'
import { isRewindJobCopiesEditable } from '../../helpers/is-rewind-job-copies-editable'

interface RewindCopiesModalProps {
    shipment: Shipment
    open: boolean
    onClose: () => void
}

const RewindCopiesModal: FC<RewindCopiesModalProps> = ({ shipment, open, onClose }) => {
    const dispatch = useAppDispatch()
    const [values, setValues] = useState<Record<DocumentId, number | null>>({})
    const { data, isFetching } = useGetRewindJobsQuery(
        {
            filters: { shipment: { documentId: { $eq: shipment.documentId } } },
            populate: {
                shipment: { populate: { shipment_positions: true } },
                rewind_queue_entries: { populate: { machine: true } },
            },
            sort: ['id:asc'],
            pagination: { pageSize: 100, page: 1 },
        },
        { skip: !open }
    )
    const [updateCopies, updateCopiesState] = useUpdateRewindJobsCopiesMutation()
    const rewindJobs = useMemo(() => data?.data || [], [data?.data])
    const shipmentCopies = getShipmentCopies(shipment)
    const sum = rewindJobs.reduce(
        (acc, job) => acc + (values[job.documentId] ?? getRewindJobCopies(job)),
        0
    )
    const difference = sum - shipmentCopies
    const changed = rewindJobs.filter(
        job => values[job.documentId] != null && values[job.documentId] !== getRewindJobCopies(job)
    )

    useErrorModal(updateCopiesState, 'Ошибка изменения тиража частей')

    useEffect(() => {
        if (open) setValues({})
    }, [open])

    const onOk = async () => {
        const result = await updateCopies(
            changed.map(job => ({ documentId: job.documentId, copies: values[job.documentId]! }))
        )

        if ('data' in result) {
            dispatch(rewindQueueEntriesApi.util.invalidateTags(['RewindQueueEntries']))
            onClose()
        }
    }

    const columns: ColumnsType<RewindJob> = [
        { dataIndex: 'id', title: '№', width: 60 },
        {
            key: 'machines',
            title: 'Станок',
            render: (_value, record) =>
                (record.rewind_queue_entries || [])
                    .map(entry => entry.machine?.name)
                    .filter(Boolean)
                    .join(', ') || '—',
        },
        { dataIndex: 'jobStatus', title: 'Статус', width: 130 },
        {
            key: 'copies',
            title: 'Тираж',
            width: 140,
            render: (_value, record) =>
                isRewindJobCopiesEditable(record) ? (
                    <InputNumber
                        min={1}
                        precision={0}
                        value={values[record.documentId] ?? getRewindJobCopies(record)}
                        onChange={value =>
                            setValues(prev => ({ ...prev, [record.documentId]: value }))
                        }
                    />
                ) : (
                    formatNumber(getRewindJobCopies(record))
                ),
        },
    ]

    return (
        <Modal
            title='Тираж по станкам'
            open={open}
            width={640}
            onOk={onOk}
            okText='Сохранить'
            okButtonProps={{
                disabled:
                    difference !== 0 ||
                    changed.length === 0 ||
                    changed.some(job => !values[job.documentId]),
            }}
            confirmLoading={updateCopiesState.isLoading}
            onCancel={onClose}
        >
            <Table
                dataSource={rewindJobs}
                columns={columns}
                rowKey='documentId'
                size='small'
                pagination={false}
                loading={isFetching}
            />
            <Typography.Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
                Сумма: {formatNumber(sum)} из {formatNumber(shipmentCopies)}
                {difference !== 0 && (
                    <Typography.Text type='danger'>
                        {' '}
                        ({difference > 0 ? 'больше' : 'меньше'} на{' '}
                        {formatNumber(Math.abs(difference))})
                    </Typography.Text>
                )}
            </Typography.Paragraph>
        </Modal>
    )
}

export default RewindCopiesModal
